import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const NotificationContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [checkInterval, setCheckIntervalId] = useState(null);

  // Check if notifications are supported
  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      setIsEnabled(Notification.permission === 'granted' && localStorage.getItem('weatherNotifications') === 'true');
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;
    
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setIsEnabled(true);
        localStorage.setItem('weatherNotifications', 'true');
        
        // Show a test notification
        showNotification('Notifications Enabled', {
          body: 'You will now receive weather alerts for your farm',
          tag: 'test-notification'
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }, [isSupported]);

  // Show a notification
  const showNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') return;
    
    const defaultOptions = {
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      vibrate: [200, 100, 200],
      ...options
    };
    
    // Try using service worker notifications (more reliable)
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(title, defaultOptions);
      });
    } else {
      // Fallback to regular Notification API
      new Notification(title, defaultOptions);
    }
  }, [isSupported, permission]);

  // Check weather alerts and notify
  const checkWeatherAlerts = useCallback(async (token) => {
    if (!isEnabled || !token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/weather/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const alerts = response.data.alerts || [];
      const severeAlerts = alerts.filter(a => a.severity === 'high' || a.severity === 'medium');
      
      // Check if we've already notified about these alerts
      const notifiedAlerts = JSON.parse(localStorage.getItem('notifiedAlerts') || '[]');
      
      severeAlerts.forEach(alert => {
        const alertKey = `${alert.type}-${alert.title}`;
        
        if (!notifiedAlerts.includes(alertKey)) {
          showNotification(`⚠️ ${alert.title}`, {
            body: alert.message,
            tag: `weather-${alert.type}`,
            requireInteraction: alert.severity === 'high',
            data: { url: '/app/weather' }
          });
          
          // Mark as notified
          notifiedAlerts.push(alertKey);
          localStorage.setItem('notifiedAlerts', JSON.stringify(notifiedAlerts.slice(-20))); // Keep last 20
        }
      });
    } catch (error) {
      console.error('Failed to check weather alerts:', error);
    }
  }, [isEnabled, showNotification]);

  // Start/stop periodic weather checks
  const enableWeatherNotifications = useCallback(async (token) => {
    const granted = await requestPermission();
    if (!granted) return false;
    
    setIsEnabled(true);
    localStorage.setItem('weatherNotifications', 'true');
    
    // Clear old notified alerts when re-enabling
    localStorage.removeItem('notifiedAlerts');
    
    // Initial check
    checkWeatherAlerts(token);
    
    // Set up periodic checks (every 30 minutes)
    if (checkInterval) clearInterval(checkInterval);
    const intervalId = setInterval(() => checkWeatherAlerts(token), 30 * 60 * 1000);
    setCheckIntervalId(intervalId);
    
    return true;
  }, [requestPermission, checkWeatherAlerts, checkInterval]);

  const disableWeatherNotifications = useCallback(() => {
    setIsEnabled(false);
    localStorage.setItem('weatherNotifications', 'false');
    
    if (checkInterval) {
      clearInterval(checkInterval);
      setCheckIntervalId(null);
    }
  }, [checkInterval]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [checkInterval]);

  return (
    <NotificationContext.Provider value={{
      permission,
      isSupported,
      isEnabled,
      requestPermission,
      showNotification,
      enableWeatherNotifications,
      disableWeatherNotifications,
      checkWeatherAlerts
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
